import React, { useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import _, { get, isArray } from 'lodash'
import * as api from '../services/api'
import {
  CROPPED,
  UPLOADED,
  RESIZED,
  normalizeUploadSession,
} from '../utils/upload'
import useProductData from '../hooks/useProductData'
import { Logic } from '../utils/logic'
import { getFrameAspect, getReverseFrameAspect } from '../utils/image'
import { ProductListContext } from './ProductListContext'
import { separateLogics } from '../utils/helper'

const OrderFormContext = React.createContext({
  batch: null,
  frameSize: null,
  setFrameSize: () => {
  },
  availableShapes: [],
  availableFrameSizes: [],
  availableProductAttributes: [],
  images: [],
  setImages: () => {
  },
  addUploadedImage: () => {
  },
  updateUploadedImage: () => {
  },
  validateUploadedImages: () => {
  },
  selectedAttributes: {},
  selectedShape: 1,
  hoveredAttribute: {},
  hoveredFrameSize: {},
  setSelectedAttribute: () => {
  },
  setSelectedShape: () => {
  },
  clearSelectedAttributes: () => {
  },
  clearSelectedAttribute: () => {
  },
  loadProductAttributes: () => {
  },
  submitForm: () => {
  },
  isLoading: false,
  error: null,
  submitFormSuccess: false,
  loadOrderFormData: () => {
  },
  isSubmitting: false,
  showCartModal: false,
  showHoveredAttribute: false,
  showHoveredFrameSize: false,
  totalPrice: 0,
  setTotalPrice: () => {
  },
  setHoveredAttribute: () => {
  },
  setHoveredFrameSize: () => {
  },
  setShowHoveredAttribute: () => {
  },
  setShowHoveredFrameSize: () => {
  },
  fetchImagesFromSession: () => {
  },
  fetchImagesFromBatch: () => {
  },
  createAndStoreSession: () => {
  },
  createAndStoreBatch: () => {
  },
  updateImageQuantity: () => {
  },
  updateRemovedImage: () => {
  },
  getSessionId: () => {
  },
  storeSessionId: () => {
  },
  removeSessionId: () => {
  },
  validate: () => {
  },
  isAspectRatioCorrect: () => {
  },
  comment: '',
  setComment: () => {
  },
  checkAttributeLogic: () => {
  },
  checkOptionLogic: () => {
  },
  resetData: () => {
  },
  getQuantitySetFromFrameSize: () => {
  },
  layouts: [],
  setLayouts: () => {},
  isExtendedUploaderArea: true,
  setIsExtendedUploaderArea: () => {},
})

const OrderFormContextProvider = ({ product, children }) => {
  const [signature, setSignature] = useState(null)
  const [box, setBox] = useState(null)
  const [layouts, setLayouts] = useState([])
  const [isExtendedUploaderArea, setIsExtendedUploaderArea] = React.useState(true)

  const productListContext = useContext(ProductListContext)

  const {
    formModalVisible,
  } = productListContext

  useEffect(() => {
    if (formModalVisible) _resetStatus()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formModalVisible])

  const {
    batch,
    setBatch,
    formTitle,
    images,
    frameSize,
    selectedAttributes,
    selectedShape,
    hoveredAttribute,
    hoveredFrameSize,
    availableFrameSizes,
    availableProductAttributes,
    availableProductAttributesBySizes,
    availableShapes,
    totalPrice,
    status,
    comment,
    showCartModal,
    showHoveredAttribute,
    showHoveredFrameSize,
    setFormTitle,
    setImages,
    setFrameSize,
    setSelectedAttributes,
    setHoveredAttribute,
    setHoveredFrameSize,
    setAvailableFrameSizes,
    setAvailableProductAttributes,
    setAvailableProductAttributesBySizes,
    setAvailableShapes,
    setTotalPrice,
    setStatus,
    setShowCartModal,
    setSelectedShape,
    setComment,
    setShowHoveredAttribute,
    setShowHoveredFrameSize,
    resetData,
    is2mmSubstrateSelected
  } = useProductData(product)

  const validateUploadedImages = () => {
    if (images.length === 0) {
      return false
    }

    for (let i = 0; i < images.length; i++) {
      if (!_verifyImage(images[i])) {
        return false
      }
    }

    return true
  }

  const _verifyImage = (image) => {
    if (!image.aspect) {
      return false
    }

    if ([CROPPED, UPLOADED].includes(image.status)) {
      return true
    }

    return image.aspect === frameSize.frame_aspect_ratio ||
            image.aspect.split(':').reverse().join(':') ===
            frameSize.frame_aspect_ratio
  }

  const resetImagesState = () => {
    setImages(
      images.map(image => {
        delete image.cropped

        return { ...image, status: RESIZED }
      }),
    )
  }

  const addUploadedImage = (image) => {
    image = normalizeUploadSession(image)
    setImages([...images, { ...image, amount: 1 }])
  }

  const updateUploadedImage = (session, updateData) => {
    const image = images.find(img => img.session === session)
    const index = images.findIndex(img => img.session === session)

    if (image) images[index] = { ...image, ...updateData }

    setImages(images)
  }

  const setSelectedAttribute = (attribute, value) => {
    const excludePreviousAttributeWithSameId = selectedAttributes.filter(
      ({ id }) => id !== attribute.id)

    setSelectedAttributes(
      [...excludePreviousAttributeWithSameId, { ...attribute, value }])
  }

  const clearSelectedAttribute = attribute => {
    setSelectedAttributes(selectedAttributes.filter(
      ({ id }) => id !== attribute.id
    ))
  }

  const clearSelectedAttributes = () => {
    setSelectedAttributes([])
  }

  const setIsLoading = isLoading => setStatus({ ...status, isLoading })
  const setIsLoadingAttribute = isLoadingAttribute => setStatus(
    { ...status, isLoadingAttribute })

  const loadOrderFormData = async () => {
    setIsLoading(true)

    try {
      const { frames, shapes, title } = await api.fetchFrameSizes(product)

      setFormTitle(title)
      setAvailableFrameSizes(frames)
      setAvailableShapes(shapes)

      if (!selectedShape) {
        setSelectedShape(shapes[0])
      } else if (frameSize) {
        // Display shape which own the selected frame size instead of previous selected shape.
        const shapesOwnFrameSize = frameSize.shapes

        if (shapesOwnFrameSize && shapesOwnFrameSize.length >
                    0) setSelectedShape(shapesOwnFrameSize[0])
      }

      await maybeFetchImagesFromBatch()

      setIsLoading(false)
    } catch (e) {
      setStatus({ ...status, error: e })

      setIsLoading(false)
    }
  }

  const loadProductAttributes = async () => {
    try {
      setStatus({ ...status, isLoadingAttribute: true })
      setIsLoadingAttribute(true)

      const { attributes, isNewAttributes } = await _loadProductAttributes()

      if (isNewAttributes) {
        setAvailableProductAttributesBySizes({
          ...availableProductAttributesBySizes,
          [`${frameSize.name}`]: attributes,
        })
      }

      setAvailableProductAttributes(attributes)
      setStatus({ ...status, isLoadingAttribute: false })
    } catch (e) {
      setStatus({ ...status, isLoadingAttribute: false })
    }
  }

  const createNewUploadBatch = async () => {
    try {
      const batch = await api.createBatch()

      setBatch(batch)

      return batch
    } catch (error) {
      console.log(error)
    }
  }

  const getBatchByUser = async () => {
    try {
      const batch = await api.getBatchByUser()
      return batch
    } catch (error) {
      console.log(error)
    }
  }

  const updateImageQuantity = async (quantity, uuid) => {
    try {
      await api.updateFileAmount({ quantity, uuid })
    } catch (error) {
      setStatus({ ...status, error })
    }
  }

  const updateRemovedImage = async (uuid) => {
    try {
      await api.removeFile(uuid)
    } catch (error) {
      setStatus({ ...status, error })
    }
  }

  const maybeFetchImagesFromBatch = async () => {
    try {
      const batch = await createNewUploadBatch()

      const fetchBatchData = batch
        ? await api.fetchBatchData(batch)
        : { status: 'new' }

      if (fetchBatchData.status === 'uploading') {
        const files = _.get(fetchBatchData, 'files')

        const REMOVED_STATUS = 'removed'

        /* eslint-disable-next-line no-unused-vars */
        const images = files.filter(({ status }) => status !==
                    REMOVED_STATUS) // Only display images which are not removed.
          .map(file => {
            const {
              cropped_metadata: croppedMetadata,
              original_metadata: originalMetadata,
              preview_metadata: previewMetadata,
              thumbnail_metadata: thumbnailMetadata,
              expires, uuid, status,
            } = file

            const image = {
              data: {
                original: originalMetadata,
                thumbnail: thumbnailMetadata,
                preview: previewMetadata,
                cropped: croppedMetadata,
              },
              expiration: expires,
              session: uuid,
              status,
              success: true,
            }

            return {
              ...normalizeUploadSession(image),
              amount: file.quantity,
            }
          })

        setImages(images)
        return
      }
    } catch (err) {
      const batchNotFound = get(err, 'response.status') === 404

      if (batchNotFound) {
        await createNewUploadBatch()
      }
    }
  }

  const submitForm = async (data) => {
    setStatus({
      ...status,
      submitFormSuccess: false,
      isSubmitting: true,
    })

    try {
      const batch = await getBatchByUser()

      // Hotfix PM-235
      if (_.isEmpty(data.photos)) {
        data.photos = getBatchByUser()
        data.photoBug = 'yes'
      }

      data.version = '2022-06-01'

      try {
        const oja_files = images.map((img) => ({
          uuid: img.session,
          status: img.status,
          quantity: img.amount,
        }))

        const completeResponse = await api.completeBatch(batch.key, { oja_files })
        if (!completeResponse.success) {
          throw new Error()
        }
      } catch (e) {
        setStatus({
          error: 'Could not process your cart at moment, please try again.',
          isSubmitting: false
        })

        return
      }

      const submitOrderFormRes = await api.submitOrderForm(data)

      const { status, message } = submitOrderFormRes

      if (status !== 'success') {
        setStatus({
          ...status,
          error: message,
          isSubmitting: false,
        })
        return
      }

      resetData()
      _removeCacheData(product)

      setStatus({
        ...status,
        submitFormSuccess: true,
        isSubmitting: false,
      })

      setShowCartModal(false)
    } catch (e) {
      setStatus({
        ...status,
        error: e,
        isSubmitting: false,
      })
    }
  }

  const getQuantitySetFromFrameSize = _frameSize => {
    if (shouldUsePackagedPrintsException()) return 20

    if (_frameSize.hasOwnProperty('quantity_set')) {
      return _frameSize.quantity_set
    }

    return 0
  }

  const shouldUsePackagedPrintsException = () => {
    return selectedAttributes.filter(attribute => {
      return attribute.code === 'packaged-prints-paper-type' &&
                ['Satin', 'Matte Art paper', 'Economy'].includes(attribute.value)
    }).length === 1
  }

  const getTotalImages = _images => {
    return _images.reduce((n, { amount }) => n + amount, 0)
  }

  const isValidQuantity = (_frameSize, images) => {
    const quantitySet = getQuantitySetFromFrameSize(_frameSize)

    if (isNaN(quantitySet) || !quantitySet) {
      return true
    }

    const totalImages = getTotalImages(images)

    if (isNaN(totalImages) || !totalImages) {
      return false
    }

    return totalImages % quantitySet === 0
  }

  const validate = () => {
    let validAttributes = true
    // let validImages = true
    let validFrameSize = true
    let validQuantitySet = true
    let errorMessage = []

    if (!frameSize) {
      validFrameSize = false
      errorMessage = [...errorMessage, 'Frame size is required.']
    }

    availableProductAttributes.forEach(attribute => {
      /**
             * Validate attribute required
             */
      const attributeRequired = attribute.is_required

      if (attributeRequired) {
        const requiredAttributeHasValue = selectedAttributes.filter(
          ({ value, code }) =>
            code === attribute.code && !!value,
        ).length > 0

        if (!requiredAttributeHasValue) {
          let shouldHasValue = attribute.logic
            ? (new Logic(attribute.logic)).evaluate(selectedAttributes)
            : true

          // Hot fix PM-232
          if (attribute.label === 'Mat Colour') {
            shouldHasValue = true
          }

          if (attribute.label === 'Window') {
            shouldHasValue = true
          }

          if (
            attribute.label === 'Substrate' &&
                        selectedShape !== 'BOXED'
          ) {
            shouldHasValue = true
          }

          if (attribute.label === 'Leather Strap') {
            if (selectedShape !== 'CIRCLE' || frameSize.name === 'woodland-blocks_10x10cm-circle') {
              shouldHasValue = false
            }
          }

          if (shouldHasValue) {
            validAttributes = false
            errorMessage = [
              ...errorMessage,
                            `${attribute.label} is required.`,
            ]
          }
        }
      }
    })

    /**
         * Validate quantity
         */
    if (!isValidQuantity(frameSize, images)) {
      validQuantitySet = false
      errorMessage = [
        ...errorMessage,
                `Please upload in multiples of ${getQuantitySetFromFrameSize(frameSize)}.`,
      ]
    }

    return {
      status: !!validFrameSize && !!validAttributes &&
                !!validQuantitySet,
      message: errorMessage,
    }
  }

  const isAspectRatioCorrect = (aspect, frameSize, cropped) => {
    if (!aspect || !frameSize) {
      return false
    }

    const frameSizeAspectMatter = frameSize.frame_aspect_ratio !== '0:0'
    if (!frameSizeAspectMatter) {
      return true
    }

    const croppedImageSizes = get(cropped, 'props.image_sizes')
    if (isArray(croppedImageSizes) && croppedImageSizes.length === 2) {
      const canRotate = !!frameSize.can_rotate
      const frameSizeAspectRatio = getFrameAspect(frameSize).toFixed(2)
      const frameSizeAspectReverseRatio = getReverseFrameAspect(frameSize).toFixed(2)

      const croppedWidth = croppedImageSizes[0]
      const croppedHeight = croppedImageSizes[1]

      const isMatchAspectRatio = frameSizeAspectRatio ===
                (croppedWidth / croppedHeight).toFixed(2)
      const isMatchReverseAspectRatio = frameSizeAspectReverseRatio ===
                (croppedWidth / croppedHeight).toFixed(2)

      return canRotate
        ? isMatchAspectRatio || isMatchReverseAspectRatio
        : isMatchAspectRatio
    }

    return aspect === frameSize.frame_aspect_ratio ||
            aspect.split(':').reverse().join(':') === frameSize.frame_aspect_ratio
  }

  const checkAttributeLogic = (attribute) => {
    if (Object.prototype.hasOwnProperty.call(attribute, 'logic') &&
            attribute.logic) {
      const { shapeLogics, attributeLogics, sizeLogics } = separateLogics(
        attribute.logic)

      return (new Logic(attributeLogics)).evaluate(selectedAttributes) &&
                (new Logic(shapeLogics)).evaluate([{ code: 'shape', value: selectedShape }]) &&
                (new Logic(sizeLogics)).evaluate([{ code: 'size', value: _.get(frameSize, 'name') }])
    }

    return true
  }

  const checkOptionLogic = (option) => {
    if (Object.prototype.hasOwnProperty.call(option, 'logic') && option.logic) {
      const { sizeLogics } = separateLogics(option.logic)

      return (new Logic(sizeLogics)).evaluate(
        [{ code: 'size', value: _.get(frameSize, 'name') }])
    }

    return true
  }

  const _removeCacheData = (product) => {
    localStorage.removeItem(product)
  }

  const _resetStatus = () => {
    setStatus({
      ...status,
      submitFormSuccess: false,
      isSubmitting: false,
      error: '',
    })
  }

  const _loadProductAttributes = async () => {
    let attributes = _.get(availableProductAttributesBySizes, frameSize.name)

    if (attributes) return { attributes, isNewAttributes: false }

    attributes = await api.fetchProductAttributes(product, frameSize.name)

    return { attributes, isNewAttributes: true }
  }

  const shouldLoadBoxPrice = () => {
    if (frameSize !== null) {
      return isValidQuantity(frameSize, images) === true &&
                (selectedShape === 'BOXED' || selectedShape === 'ENVELOPE') &&
                isPaperTypeSelected() === true
    }

    return false
  }

  const isPaperTypeSelected = () => {
    return selectedPaperType() !== null
  }

  const selectedPaperType = () => {
    const selected = selectedAttributes.filter(attribute => {
      return attribute.code.indexOf('paper-type') !== -1
    })

    return selected.length === 1 ? selected[0] : null
  }

  // PM-376
  useEffect(() => {
    if (shouldLoadBoxPrice()) {
      api.getBoxPrice({
        product: product,
        quantity: getTotalImages(images),
        shape: selectedShape,
        options: {
          'Print Size': frameSize.title,
          'Paper Type': selectedPaperType().value
        }
      }).then(response => {
        if (response.success === true) {
          setBox(response.data)
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images, frameSize, selectedShape, selectedAttributes])

  const isMattedPrintsSelected = product.indexOf('matted-print') !== -1

  const selectedMattingType = () => {
    const selected = selectedAttributes.filter(attribute => {
      return attribute.code.indexOf('matting-type') !== -1
    })

    return selected.length === 1 && selected[0]?.value !== 'No mat or spacers' ? selected[0] : null
  }

  const isMattingStuff = (isMattedPrintsSelected && selectedPaperType()) || selectedMattingType()

  const productsLimitUpload = {
    // The key is product key, the value is number of quantity can be uploaded
    'stickers-stock': 1

    // The other one can be unlimited upload
  }

  // If this value is null, that means unlimited quantity
  const maxQuantity = productsLimitUpload[product] || null

  const values = {
    batch,
    setBatch,
    formTitle,
    images,
    frameSize,
    selectedAttributes,
    selectedShape,
    hoveredAttribute,
    hoveredFrameSize,
    availableFrameSizes,
    availableProductAttributes,
    availableShapes,
    totalPrice,
    ...status,
    showCartModal,
    showHoveredAttribute,
    showHoveredFrameSize,
    setFrameSize,
    setImages,
    resetImagesState,
    addUploadedImage,
    updateUploadedImage,
    setSelectedAttribute,
    clearSelectedAttributes,
    clearSelectedAttribute,
    loadOrderFormData,
    submitForm,
    loadProductAttributes,
    setShowCartModal,
    setTotalPrice,
    createAndStoreBatch: createNewUploadBatch,
    maybeFetchImagesFromBatch,
    updateImageQuantity,
    updateRemovedImage,
    setHoveredAttribute,
    setHoveredFrameSize,
    validateUploadedImages,
    getBatchByUser,
    product,
    setSelectedShape,
    validate,
    isAspectRatioCorrect,
    comment,
    setComment,
    setShowHoveredAttribute,
    setShowHoveredFrameSize,
    checkAttributeLogic,
    checkOptionLogic,
    getQuantitySetFromFrameSize,
    signature,
    setSignature,
    box,
    setBox,
    shouldLoadBoxPrice,
    is2mmSubstrateSelected,
    layouts,
    setLayouts,
    isExtendedUploaderArea,
    setIsExtendedUploaderArea,
    selectedMattingType,
    isMattedPrintsSelected,
    isMattingStuff,
    selectedPaperType,
    productsLimitUpload,
    maxQuantity
  }

  return (
    <OrderFormContext.Provider value={values}>
      {children}
    </OrderFormContext.Provider>
  )
}

OrderFormContextProvider.propTypes = {
  product: PropTypes.string,
  children: PropTypes.any,
}

export {
  OrderFormContext,
  OrderFormContextProvider,
}
